import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card, Form, Input, Button, DatePicker, TimePicker,
  Switch, Upload, Row, Col, Divider,
  Space, Typography, Table, Tag, Tooltip, App,
  Modal, Select, Popconfirm, InputNumber, Tabs } from 'antd';
import PujaSpinner from '../../components/PujaSpinner';
import {
  ArrowLeftOutlined, SaveOutlined,
  CalendarOutlined, PlusOutlined,
  CheckSquareOutlined, TeamOutlined,
  EditOutlined, DeleteOutlined, UploadOutlined, PictureOutlined,
} from '@ant-design/icons';
import { eventoService, animalService, categoriaService, sponsorService, fixUrl, API_HOST } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import CondicionesDrawer from './CondicionesDrawer';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

const EventoForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [form] = Form.useForm();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [condSeccion, setCondSeccion] = useState(null);
  
  // Catálogo PDF del evento (lo abre el enlace 'Catálogo PDF' del micrositio)
  const [catalogoPdfUrl, setCatalogoPdfUrl] = useState(null);
  const [catalogoModal, setCatalogoModal] = useState(false);
  const [catalogoFile, setCatalogoFile] = useState(null);
  const [savingCatalogo, setSavingCatalogo] = useState(false);

  const isEditing = !!id;

  useEffect(() => {
    if (isEditing) {
      fetchEvento();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchEvento = async () => {
    setLoading(true);
    try {
      const response = await eventoService.getById(id);
      const evento = response.data;
      
      form.setFieldsValue({
        nombre_evento: evento.nombre_evento,
        fecha_inicio: evento.fecha_inicio ? dayjs(evento.fecha_inicio) : null,
        fecha_fin: evento.fecha_fin ? dayjs(evento.fecha_fin) : null,
        hora_inicio: evento.hora_inicio ? dayjs(evento.hora_inicio, 'HH:mm:ss') : null,
        hora_fin: evento.hora_fin ? dayjs(evento.hora_fin, 'HH:mm:ss') : null,
        envio_email: evento.envio_email === 1,
        muestra_ganadores: evento.muestra_ganadores === 1,
        // Default alineado con el del backend. Un evento viejo, creado antes de
        // que la columna existiera, tiene el valor por server_default igual —
        // pero si la respuesta viniera sin el campo, mostrar el mismo default
        // evita que abrir y guardar sin tocar nada cambie el permiso.
        analytics_compartidas: evento.analytics_compartidas || 'agregado',
        estado_evento: evento.estado_evento === 1,
        ofertas_activas: evento.ofertas_activas === 1,
        // mostrar_btn_acceso vive en config; default true (mostrar) si no está
        mostrar_btn_acceso: (evento.configuracion_micrositio?.mostrar_btn_acceso ?? true),
        // Ventana de pre-ofertas: también en config, ISO con offset.
        ofertas_desde: evento.configuracion_micrositio?.ofertas_desde
          ? dayjs(evento.configuracion_micrositio.ofertas_desde) : null,
        ofertas_hasta: evento.configuracion_micrositio?.ofertas_hasta
          ? dayjs(evento.configuracion_micrositio.ofertas_hasta) : null,
        mensaje_oferta_1: evento.mensaje_oferta_1 || '',
        mensaje_oferta_2: evento.mensaje_oferta_2 || '',
      });
      
      // LAS IMAGENES DEL MICROSITIO (logo, fondo del hero, isotipo, animales
      // sin foto) ya no se cargan aca: viven en Herramientas → Micrositio,
      // con el resto del diseño (2026-10-06). Este formulario es el EVENTO.
      const cfg = evento.configuracion_micrositio || {};
      setCatalogoPdfUrl(cfg.catalogo_pdf_url || null);
    } catch (error) {
      message.error('Error al cargar el evento');
      navigate('/eventos');
    }
    setLoading(false);
  };

  // Guardar el catálogo PDF (sube vía update del evento → configuracion_micrositio)
  const handleGuardarCatalogo = async () => {
    if (!catalogoFile) { setCatalogoModal(false); return; }
    setSavingCatalogo(true);
    try {
      const fd = new FormData();
      fd.append('catalogo_pdf', catalogoFile);
      await eventoService.update(id, fd);
      // El update devuelve to_dict() sin la config; recargamos para la URL nueva.
      const { data } = await eventoService.getById(id);
      setCatalogoPdfUrl(data.configuracion_micrositio?.catalogo_pdf_url || null);
      setCatalogoFile(null);
      setCatalogoModal(false);
      message.success('Catálogo PDF guardado');
    } catch {
      message.error('Error al guardar el catálogo PDF');
    }
    setSavingCatalogo(false);
  };

  const handleQuitarCatalogo = async () => {
    setSavingCatalogo(true);
    try {
      const fd = new FormData();
      fd.append('catalogo_pdf_remove', '1');
      await eventoService.update(id, fd);
      setCatalogoPdfUrl(null);
      setCatalogoFile(null);
      message.success('Catálogo PDF eliminado');
    } catch {
      message.error('Error al eliminar el catálogo PDF');
    }
    setSavingCatalogo(false);
  };

  const onFinish = async (values) => {
    setSaving(true);
    
    // Crear FormData para enviar archivos
    const formData = new FormData();
    formData.append('nombre_evento', values.nombre_evento);
    formData.append('fecha_inicio', values.fecha_inicio?.format('YYYY-MM-DD'));
    formData.append('fecha_fin', values.fecha_fin?.format('YYYY-MM-DD'));
    
    if (values.hora_inicio) {
      formData.append('hora_inicio', values.hora_inicio.format('HH:mm:ss'));
    }
    if (values.hora_fin) {
      formData.append('hora_fin', values.hora_fin.format('HH:mm:ss'));
    }
    
    formData.append('envio_email', values.envio_email ? 1 : 0);
    formData.append('muestra_ganadores', values.muestra_ganadores ? 1 : 0);
    formData.append('estado_evento', values.estado_evento ? 1 : 0);
    formData.append('ofertas_activas', values.ofertas_activas ? 1 : 0);
    // Ventana de pre-ofertas. Se manda ISO CON OFFSET a proposito: el backend
    // corre en UTC, y un '09:00' pelado se leeria como 09:00 UTC = 06:00 de
    // Argentina. Las ofertas abririan tres horas antes de lo anunciado.
    formData.append('ofertas_desde', values.ofertas_desde ? values.ofertas_desde.format() : '');
    formData.append('ofertas_hasta', values.ofertas_hasta ? values.ofertas_hasta.format() : '');
    formData.append('mostrar_btn_acceso', values.mostrar_btn_acceso ? 1 : 0);  // → config
    // Sin `|| 'agregado'`, un form que por lo que sea no tenga el campo mandaria
    // undefined y el backend lo normalizaria igual — pero prefiero que el valor
    // que viaja sea siempre explicito: es un permiso, no una preferencia.
    formData.append('analytics_compartidas', values.analytics_compartidas || 'agregado');
    
    if (values.mensaje_oferta_1 !== undefined) {
      formData.append('mensaje_oferta_1', values.mensaje_oferta_1 || '');
    }
    if (values.mensaje_oferta_2 !== undefined) {
      formData.append('mensaje_oferta_2', values.mensaje_oferta_2 || '');
    }
    
    try {
      if (isEditing) {
        await eventoService.update(id, formData);
        message.success('Evento actualizado exitosamente');
      } else {
        await eventoService.create(formData);
        message.success('Evento creado exitosamente');
      }
      navigate('/eventos');
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al guardar el evento');
    }
    
    setSaving(false);
  };

  // Validar que fecha fin sea mayor o igual a fecha inicio
  const validateFechaFin = (_, value) => {
    const fechaInicio = form.getFieldValue('fecha_inicio');
    if (value && fechaInicio && value.isBefore(fechaInicio)) {
      return Promise.reject(new Error('La fecha de fin debe ser igual o posterior a la fecha de inicio'));
    }
    return Promise.resolve();
  };

  // Botón de upload

  if (loading) {
    return (
      <PujaSpinner size="lg" />
    );
  }

  return (
    <div>
      <Button 
        icon={<ArrowLeftOutlined />} 
        onClick={() => navigate('/eventos')}
        style={{ marginBottom: 16 }}
      >
        Volver a Eventos
      </Button>

      <Card>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
          <Title level={2} style={{ margin: 0 }}>
            <CalendarOutlined /> {isEditing ? 'Editar Evento' : 'Nuevo Evento'}
          </Title>
          {isEditing && (
            <Space wrap>
              <Button onClick={() => setCondSeccion('comercial')}>Cond. Comerciales</Button>
              <Button onClick={() => setCondSeccion('sanitaria')}>Cond. Sanitarias</Button>
              <Button onClick={() => setCondSeccion('reclamos')}>Reclamos y Garantías</Button>
              <Button onClick={() => setCondSeccion('garantia')} type="dashed">📄 Garantía (PDF)</Button>
              <Button onClick={() => setCatalogoModal(true)} type="dashed">📕 Catálogo (PDF)</Button>
            </Space>
          )}
        </div>

        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          initialValues={{
            estado_evento: true,
            envio_email: false,
            muestra_ganadores: false,
            ofertas_activas: false,
            mostrar_btn_acceso: true,
            analytics_compartidas: 'agregado',
          }}
        >
          {/* Información básica */}
          <Divider orientation="left">Información del Evento</Divider>
          
          <Row gutter={16}>
            <Col xs={24}>
              <Form.Item
                name="nombre_evento"
                label="Nombre del Evento"
                rules={[{ required: true, message: 'Ingrese el nombre del evento' }]}
              >
                <Input placeholder="Ej: Remate Primavera 2026" size="large" />
              </Form.Item>
            </Col>
          </Row>

          {/* Fechas y horarios */}
          <Divider orientation="left">Fechas y Horarios</Divider>
          
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="fecha_inicio"
                label="Fecha de Inicio"
                rules={[{ required: true, message: 'Seleccione fecha de inicio' }]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  placeholder="Seleccione fecha"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="fecha_fin"
                label="Fecha de Fin"
                rules={[
                  { required: true, message: 'Seleccione fecha de fin' },
                  { validator: validateFechaFin }
                ]}
              >
                <DatePicker 
                  style={{ width: '100%' }} 
                  format="DD/MM/YYYY"
                  placeholder="Seleccione fecha"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="hora_inicio"
                label="Hora de Inicio"
              >
                <TimePicker 
                  style={{ width: '100%' }} 
                  format="HH:mm"
                  placeholder="Seleccione hora"
                  size="large"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="hora_fin"
                label="Hora de Fin"
              >
                <TimePicker 
                  style={{ width: '100%' }} 
                  format="HH:mm"
                  placeholder="Seleccione hora"
                  size="large"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Opciones */}
          <Divider orientation="left">Opciones</Divider>
          
          <Row gutter={24}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="estado_evento"
                label="Estado del Evento"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Activo" 
                  unCheckedChildren="Inactivo"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="ofertas_activas"
                label="Ofertas Activas"
                valuePropName="checked"
                tooltip="Permite pre-ofertar en el catálogo digital"
              >
                <Switch 
                  checkedChildren="Sí" 
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={12}>
              <Form.Item
                label="Ventana de pre-ofertas"
                tooltip="Opcional. Con fechas cargadas las ofertas abren y cierran SOLAS: no hay que prender ni apagar el switch a mano. Vacío = dependen solo del switch."
              >
                <Space.Compact style={{ width: '100%' }}>
                  <Form.Item name="ofertas_desde" noStyle>
                    <DatePicker
                      showTime={{ format: 'HH:mm' }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="Abren"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                  <Form.Item
                    name="ofertas_hasta"
                    noStyle
                    dependencies={['ofertas_desde']}
                    rules={[({ getFieldValue }) => ({
                      validator(_, value) {
                        const desde = getFieldValue('ofertas_desde');
                        if (!value || !desde || value.isAfter(desde)) return Promise.resolve();
                        return Promise.reject(new Error('El cierre tiene que ser posterior a la apertura'));
                      },
                    })]}
                  >
                    <DatePicker
                      showTime={{ format: 'HH:mm' }}
                      format="DD/MM/YYYY HH:mm"
                      placeholder="Cierran"
                      style={{ width: '50%' }}
                    />
                  </Form.Item>
                </Space.Compact>
                {/* El caso que la feature promete resolver es justamente cargar
                    la fecha y olvidarse. Si el switch queda apagado no abre nada
                    y el micrositio no muestra ni el cartel ni el precio base:
                    hay que decirlo acá, no que lo descubran el sábado. */}
                <Form.Item noStyle shouldUpdate={(prev, cur) =>
                  prev.ofertas_activas !== cur.ofertas_activas ||
                  prev.ofertas_desde !== cur.ofertas_desde ||
                  prev.ofertas_hasta !== cur.ofertas_hasta}
                >
                  {({ getFieldValue }) => (
                    (getFieldValue('ofertas_desde') || getFieldValue('ofertas_hasta')) &&
                    !getFieldValue('ofertas_activas') ? (
                      <Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                        Cargaste fechas pero “Ofertas Activas” está en No: así no
                        abren solas ni se muestra el aviso en el micrositio.
                      </Text>
                    ) : null
                  )}
                </Form.Item>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="envio_email"
                label="Envío de Email"
                valuePropName="checked"
              >
                <Switch 
                  checkedChildren="Sí" 
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="muestra_ganadores"
                label="Mostrar Ganadores"
                valuePropName="checked"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="mostrar_btn_acceso"
                label="Mostrar btn Acceso"
                valuePropName="checked"
                tooltip="Muestra u oculta el botón Acceder/Mi Panel en la cabecera del micrositio (el del footer siempre está)"
              >
                <Switch
                  checkedChildren="Sí"
                  unCheckedChildren="No"
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Remate multi-cabaña */}
          <Divider orientation="left">Remate multi-cabaña</Divider>

          <Row gutter={24}>
            <Col xs={24} md={12}>
              <Form.Item
                name="analytics_compartidas"
                label="Métricas que ven las cabañas invitadas"
                tooltip="Solo aplica si invitás cabañas a consignar en este remate. Vos, como organizador, ves todo siempre."
              >
                <Select
                  options={[
                    { value: 'nada',     label: 'No compartir métricas' },
                    { value: 'agregado', label: 'El total del remate + lo suyo' },
                    { value: 'total',    label: 'Todo el detalle del remate' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              {/* Un dropdown que dice "nada / agregado / total" no le dice nada a
                  nadie, y acá la decision no es cosmetica: elegir `total` expone
                  el detalle de una cabaña ante las otras. Se explica en la misma
                  pantalla donde se elige, no en un manual que nadie lee. */}
              <Form.Item
                noStyle
                shouldUpdate={(prev, cur) =>
                  prev.analytics_compartidas !== cur.analytics_compartidas}
              >
                {({ getFieldValue }) => {
                  const nivel = getFieldValue('analytics_compartidas');
                  const texto = {
                    nada: 'La cabaña invitada no ve ninguna métrica de este remate.',
                    agregado:
                      'Ve cómo anduvo el remate en general (visitas, ofertas) y el ' +
                      'detalle de SUS animales. No ve el desempeño animal por animal ' +
                      'de las otras cabañas.',
                    total:
                      'Ve todo, incluido el detalle animal por animal de las demás ' +
                      'cabañas invitadas.',
                  }[nivel];

                  return (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 30 }}>
                      {texto}
                      {nivel === 'total' && (
                        <Text type="warning" style={{ fontSize: 12, display: 'block', marginTop: 6 }}>
                          Con esta opción, cada cabaña invitada puede ver el
                          rendimiento de los animales de las otras. Ellas lo ven
                          aclarado en su invitación.
                        </Text>
                      )}
                    </div>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          {/* Mensajes modal de oferta */}
          <Divider orientation="left">Mensajes del Modal de Oferta</Divider>
          <MensajesOfertaFields form={form} />

          {/* Botones */}
          <Divider />
          
          <Form.Item>
            <Space>
              <Button 
                type="primary" 
                htmlType="submit" 
                icon={<SaveOutlined />}
                loading={saving}
                size="large"
              >
                {isEditing ? 'Actualizar Evento' : 'Crear Evento'}
              </Button>
              <Button 
                onClick={() => navigate('/eventos')}
                size="large"
              >
                Cancelar
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* Tabla de asignación masiva — solo en edición */}
      {isEditing && <TablaAsignacionAnimales eventoId={id} />}
      {isEditing && <TablaSponsors eventoId={id} />}

      {isEditing && (
        <CondicionesDrawer
          eventoId={id}
          seccion={condSeccion}
          onClose={() => setCondSeccion(null)}
        />
      )}

      {/* Modal de carga del Catálogo PDF del evento */}
      <Modal
        title="📕 Catálogo PDF del evento"
        open={catalogoModal}
        onCancel={() => { setCatalogoModal(false); setCatalogoFile(null); }}
        onOk={handleGuardarCatalogo}
        okText="Guardar"
        confirmLoading={savingCatalogo}
        okButtonProps={{ disabled: !catalogoFile }}
      >
        <p style={{ color: '#888', fontSize: 13 }}>
          Se abre desde el enlace "Catálogo PDF" del menú del micrositio.
        </p>
        {catalogoPdfUrl && (
          <div style={{ marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: '#888' }}>PDF actual: </span>
            <a href={`${API_HOST}${catalogoPdfUrl}`} target="_blank" rel="noopener noreferrer">Ver PDF</a>
            <Button type="link" danger size="small" loading={savingCatalogo} onClick={handleQuitarCatalogo}>
              Quitar
            </Button>
          </div>
        )}
        <Upload
          accept=".pdf"
          beforeUpload={(file) => { setCatalogoFile(file); return false; }}
          maxCount={1}
          fileList={catalogoFile ? [{ uid: '1', name: catalogoFile.name, status: 'done' }] : []}
          onRemove={() => setCatalogoFile(null)}
        >
          <Button icon={<PlusOutlined />}>Seleccionar PDF</Button>
        </Upload>
      </Modal>
    </div>
  );
};

// ── Tabla de asignación masiva de animales ───────────────────────────
const TablaAsignacionAnimales = ({ eventoId }) => {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { tieneRecurso } = useAuth();
  const [catMap, setCatMap] = useState({});
  // Disponibles (sin evento)
  const [animales, setAnimales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [agrupados, setAgrupados] = useState({});
  // Vinculados (ya en el evento)
  const [vinculados, setVinculados] = useState([]);
  const [loadingVinc, setLoadingVinc] = useState(false);
  const [savingVinc, setSavingVinc] = useState(false);
  const [seleccionadosVinc, setSeleccionadosVinc] = useState([]);
  const [agrupadosVinc, setAgrupadosVinc] = useState({});
  const [mostrarComp, setMostrarComp] = useState({});
  const [mostrarCompVinc, setMostrarCompVinc] = useState({});
  const [premium, setPremium] = useState({});
  const [premiumVinc, setPremiumVinc] = useState({});

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true); setLoadingVinc(true);
    try {
      const [resDisp, resVinc, resCat] = await Promise.all([
        animalService.getSinEvento({ per_page: 500 }),
        animalService.getByEvento(eventoId),
        categoriaService.getAll(),
      ]);
      setAnimales(resDisp.data.animales || []);
      const vinc = resVinc.data.animales || resVinc.data || [];
      setVinculados(vinc);
      setSeleccionadosVinc(vinc.map(a => a.id_animal));
      setAgrupadosVinc(Object.fromEntries(vinc.map(a => [a.id_animal, a.agrupado ?? 0])));
      setMostrarCompVinc(Object.fromEntries(vinc.map(a => [a.id_animal, a.mostrar_companeros ?? 0])));
      setPremiumVinc(Object.fromEntries(vinc.map(a => [a.id_animal, a.es_premium ?? 0])));
      const cats = resCat.data.categorias || resCat.data || [];
      setCatMap(Object.fromEntries(cats.map(c => [c.id_categoria, c.nom_categoria])));
    } catch {
      message.error('Error al cargar animales');
    }
    setLoading(false); setLoadingVinc(false);
  };

  // Propaga un valor al animal tocado Y a todos los de su mismo corral (sobre
  // el state dado).
  //
  // EL ANIMAL TOCADO VA SIEMPRE. Antes la lista salia solo del filtro por
  // corral, y un animal SIN corral no matchea ni consigo mismo: el switch no
  // cambiaba nada y no decia por que. Lo destapo el tenant demo (2026-10-05),
  // cuyos animales no tienen corral; a Pilaga nunca le paso porque los suyos
  // lo tienen todos. Sin corral no hay compañeros a los que propagar, y el
  // valor queda en ese animal solo.
  const propagarPorCorral = (lista, setEstado, animal, valor) => {
    const companeros = animal.corral
      ? lista.filter(a => a.corral === animal.corral).map(a => a.id_animal)
      : [];
    const ids = [...new Set([animal.id_animal, ...companeros])];
    setEstado(prev => {
      const next = { ...prev };
      ids.forEach(id => { next[id] = valor; });
      return next;
    });
  };

  // Al marcar Agrupado se activa también "Muestra comp." por defecto (mismo corral).
  const propagarAgrupado = (lista, setEstado, setMostrar, animal, checked) => {
    propagarPorCorral(lista, setEstado, animal, checked ? 1 : 0);
    if (checked) propagarPorCorral(lista, setMostrar, animal, 1);
  };

  const handleAsignar = async () => {
    if (seleccionados.length === 0) {
      message.warning('Seleccioná al menos un animal');
      return;
    }
    setSaving(true);
    try {
      const payload = seleccionados.map(id => ({ id_animal: id, agrupado: agrupados[id] ?? 0, mostrar_companeros: mostrarComp[id] ?? 0, es_premium: premium[id] ?? 0 }));
      await animalService.asignarEventoMasivo(eventoId, payload);
      message.success(`${seleccionados.length} animales asignados al evento`);
      setSeleccionados([]); setAgrupados({}); setMostrarComp({}); setPremium({});
      cargar();
    } catch {
      message.error('Error al asignar animales');
    }
    setSaving(false);
  };

  // Guarda cambios en vinculados: desvincula los destildados y actualiza el
  // agrupado de los que quedan en el remate.
  const handleGuardarVinc = async () => {
    setSavingVinc(true);
    try {
      const sacar = vinculados.filter(a => !seleccionadosVinc.includes(a.id_animal)).map(a => a.id_animal);
      const quedan = vinculados.filter(a => seleccionadosVinc.includes(a.id_animal));
      await Promise.all(sacar.map(id => animalService.update(id, { id_evento: null })));
      if (quedan.length) {
        const payload = quedan.map(a => ({ id_animal: a.id_animal, agrupado: agrupadosVinc[a.id_animal] ?? a.agrupado ?? 0, mostrar_companeros: mostrarCompVinc[a.id_animal] ?? a.mostrar_companeros ?? 0, es_premium: premiumVinc[a.id_animal] ?? a.es_premium ?? 0 }));
        await animalService.asignarEventoMasivo(eventoId, payload);
      }
      message.success(sacar.length ? `${sacar.length} sacados del remate · cambios guardados` : 'Cambios guardados');
      cargar();
    } catch {
      message.error('Error al guardar los cambios');
    }
    setSavingVinc(false);
  };

  const columnasBase = [
    { title: 'RP', dataIndex: 'rp_animal', width: 110, render: v => <Tag>{v}</Tag> },
    { title: 'Corral', dataIndex: 'corral', width: 80, align: 'center' },
    { title: 'Nombre', dataIndex: 'nom_animal' },
    { title: 'Categoría', dataIndex: 'id_categoria', width: 200, render: id => catMap[id] || '—' },
    { title: 'Cant. corral', dataIndex: 'cantidad_corral', width: 100, align: 'center', render: v => v || '—' },
  ];

  const colAgrupado = (estado, lista, setEstado, setMostrar) => ({
    title: (
      <Tooltip title="Si el corral se muestra agrupado en el catálogo. Al activar un animal se aplica a todo el corral (y activa 'Muestra comp.'). Sin corral, aplica solo a ese animal.">
        Agrupado
      </Tooltip>
    ),
    width: 100, align: 'center',
    render: (_, record) => (
      <Switch
        size="small"
        checked={!!(estado[record.id_animal] ?? record.agrupado)}
        onChange={checked => propagarAgrupado(lista, setEstado, setMostrar, record, checked)}
        checkedChildren="Sí"
        unCheckedChildren="No"
      />
    ),
  });

  // "Muestra comp." — grilla de compañeros del corral en la ficha. Independiente
  // de Agrupado (se puede activar aunque el animal no se muestre agrupado).
  const colMuestraComp = (estado, lista, setEstado) => ({
    title: (
      <Tooltip title="Muestra la grilla con los otros animales del corral en la ficha. Se activa solo al marcar Agrupado, pero podés prenderlo/apagarlo aparte. Se aplica a todo el corral; sin corral, solo a ese animal.">
        Muestra comp.
      </Tooltip>
    ),
    width: 115, align: 'center',
    render: (_, record) => (
      <Switch
        size="small"
        checked={!!(estado[record.id_animal] ?? record.mostrar_companeros)}
        onChange={checked => propagarPorCorral(lista, setEstado, record, checked ? 1 : 0)}
        checkedChildren="Sí"
        unCheckedChildren="No"
      />
    ),
  });

  // "Premium" — destaque INDIVIDUAL (no se propaga al corral).
  const colPremium = (estado, setEstado) => ({
    title: (
      <Tooltip title="Marca el animal como Premium: muestra un label destacado en el catálogo y la ficha. Es por animal.">
        Premium
      </Tooltip>
    ),
    width: 90, align: 'center',
    render: (_, record) => (
      <Switch
        size="small"
        checked={!!(estado[record.id_animal] ?? record.es_premium)}
        onChange={checked => setEstado(prev => ({ ...prev, [record.id_animal]: checked ? 1 : 0 }))}
        checkedChildren="Sí"
        unCheckedChildren="No"
      />
    ),
  });

  const tabDisponibles = (
    <>
      <div style={{ marginBottom: 12, textAlign: 'right' }}>
        <Space>
          <Button
            icon={<CheckSquareOutlined />}
            onClick={() => setSeleccionados(animales.map(a => a.id_animal))}
            disabled={animales.length === 0}
          >
            Seleccionar todos
          </Button>
          <Button
            type="primary"
            loading={saving}
            disabled={seleccionados.length === 0}
            onClick={handleAsignar}
          >
            Asignar {seleccionados.length > 0 ? `(${seleccionados.length})` : ''} al evento
          </Button>
        </Space>
      </div>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_animal"
        dataSource={animales}
        columns={[...columnasBase, colAgrupado(agrupados, animales, setAgrupados, setMostrarComp), colMuestraComp(mostrarComp, animales, setMostrarComp), colPremium(premium, setPremium)]}
        rowSelection={{ selectedRowKeys: seleccionados, onChange: setSeleccionados, selections: [Table.SELECTION_ALL, Table.SELECTION_NONE] }}
        loading={loading}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: 'No hay animales sin evento asignado' }}
      />
    </>
  );

  const tabVinculados = (
    <>
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Text type="secondary" style={{ fontSize: 13 }}>
          Destildá un animal para sacarlo del remate y/o cambiá el switch Agrupado. Después tocá "Guardar cambios".
        </Text>
        <Button type="primary" loading={savingVinc} onClick={handleGuardarVinc}>
          Guardar cambios
        </Button>
      </div>
      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_animal"
        dataSource={vinculados}
        columns={[...columnasBase, colAgrupado(agrupadosVinc, vinculados, setAgrupadosVinc, setMostrarCompVinc), colMuestraComp(mostrarCompVinc, vinculados, setMostrarCompVinc), colPremium(premiumVinc, setPremiumVinc)]}
        rowSelection={{ selectedRowKeys: seleccionadosVinc, onChange: setSeleccionadosVinc, selections: [Table.SELECTION_ALL, Table.SELECTION_NONE] }}
        loading={loadingVinc}
        size="small"
        pagination={{ pageSize: 20, showSizeChanger: false }}
        locale={{ emptyText: 'No hay animales vinculados a este evento' }}
      />
    </>
  );

  return (
    <Card
      style={{ marginTop: 24 }}
      title={<Space><TeamOutlined /><span>Animales del evento</span></Space>}
      /* La carga por CSV de ESTE remate, desde donde uno esta pensando en el
         lote (usuario, 2026-10-26: el boton vivia solo en «ver evento» y no se
         encontraba). Lleva a la misma pantalla de carga con el remate ya
         elegido; el recurso es el de la carga masiva, no el de editar eventos. */
      extra={tieneRecurso('carga_masiva_animales') && (
        <Button icon={<UploadOutlined />} onClick={() => navigate(`/eventos/${eventoId}/carga-masiva`)}>
          Cargar por CSV en este remate
        </Button>
      )}
    >
      <Tabs
        items={[
          { key: 'disp', label: `Disponibles (${animales.length})`, children: tabDisponibles },
          { key: 'vinc', label: `Vinculados (${vinculados.length})`, children: tabVinculados },
        ]}
      />
    </Card>
  );
};

// ── Campos de mensajes del modal de oferta ──────────────────────────
const TAGS_DISPONIBLES = [
  { tag: '[nom_animal]', label: 'Nombre del animal' },
  { tag: '[corral]',     label: 'Nro. de corral' },
  { tag: '[raza]',       label: 'Raza' },
  { tag: '[categoria]',  label: 'Categoría' },
  { tag: '[nom_evento]', label: 'Nombre del evento' },
];

const TagChips = ({ fieldName, onInsert }) => (
  <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
    <span style={{ fontSize: 11, color: '#999', alignSelf: 'center', marginRight: 4 }}>
      Insertar:
    </span>
    {TAGS_DISPONIBLES.map(({ tag, label }) => (
      <Tag
        key={tag}
        color="blue"
        style={{ cursor: 'pointer', fontSize: 11 }}
        onClick={() => onInsert(fieldName, tag)}
      >
        {tag}
        <span style={{ color: '#91caff', marginLeft: 4 }}>{label}</span>
      </Tag>
    ))}
  </div>
);

const MensajesOfertaFields = ({ form }) => {
  const insertTag = (fieldName, tag) => {
    const current = form.getFieldValue(fieldName) || '';
    // Intenta insertar en la posición del cursor; como fallback, al final
    const textarea = document.querySelector(`textarea[id$="${fieldName}"]`);
    if (textarea) {
      const start = textarea.selectionStart;
      const end   = textarea.selectionEnd;
      const next  = current.slice(0, start) + tag + current.slice(end);
      form.setFieldValue(fieldName, next);
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + tag.length;
        textarea.focus();
      }, 0);
    } else {
      form.setFieldValue(fieldName, current + tag);
    }
  };

  return (
    <Row gutter={16}>
      <Col xs={24} lg={12}>
        <TagChips fieldName="mensaje_oferta_1" onInsert={insertTag} />
        <Form.Item
          name="mensaje_oferta_1"
          label="Mensaje — Animal individual"
          extra="Se muestra cuando el animal NO pertenece a un corral agrupado."
        >
          <Input.TextArea
            rows={4}
            placeholder="Ej: Al confirmar tu oferta por [nom_animal] (Corral [corral]) aceptás las condiciones del remate."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Col>
      <Col xs={24} lg={12}>
        <TagChips fieldName="mensaje_oferta_2" onInsert={insertTag} />
        <Form.Item
          name="mensaje_oferta_2"
          label="Mensaje — Corral agrupado"
          extra="Se muestra cuando el animal pertenece a un corral agrupado (la oferta aplica a todo el corral)."
        >
          <Input.TextArea
            rows={4}
            placeholder="Ej: Tu oferta por Corral [corral] aplica a todos los animales del lote. Al ganar el remate elegís un animal del corral."
            maxLength={500}
            showCount
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

// ── Tabla de sponsors / auspiciantes del evento ──────────────────────
const NIVELES_SPONSOR = [
  { value: 'gold',    label: 'Principal / Oro', size: '300×200 px' },
  { value: 'silver',  label: 'Plata',           size: '220×150 px' },
  { value: 'sponsor', label: 'Auspiciante',     size: '160×110 px' },
];

// Títulos por defecto de cada nivel (si el productor no los personaliza).
const SPONSOR_TITULOS_DEFAULT = { gold: 'Sponsors Oro', silver: 'Sponsors Plata', sponsor: 'Auspiciantes' };

const TablaSponsors = ({ eventoId }) => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [logoUrl, setLogoUrl] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [saving, setSaving] = useState(false);
  // Títulos de nivel editables (viven en configuracion_micrositio del evento)
  const [titulos, setTitulos] = useState(SPONSOR_TITULOS_DEFAULT);
  const [savingTitulos, setSavingTitulos] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const [resSpon, resEvento] = await Promise.all([
        sponsorService.getAll({ evento: eventoId }),
        eventoService.getById(eventoId),
      ]);
      setSponsors(resSpon.data || []);
      const cfg = resEvento.data.configuracion_micrositio || {};
      setTitulos({
        gold:    cfg.sponsor_titulo_gold    || SPONSOR_TITULOS_DEFAULT.gold,
        silver:  cfg.sponsor_titulo_silver  || SPONSOR_TITULOS_DEFAULT.silver,
        sponsor: cfg.sponsor_titulo_sponsor || SPONSOR_TITULOS_DEFAULT.sponsor,
      });
    } catch {
      message.error('Error al cargar sponsors');
    }
    setLoading(false);
  };

  const handleGuardarTitulos = async () => {
    setSavingTitulos(true);
    try {
      const fd = new FormData();
      fd.append('sponsor_titulo_gold', titulos.gold);
      fd.append('sponsor_titulo_silver', titulos.silver);
      fd.append('sponsor_titulo_sponsor', titulos.sponsor);
      await eventoService.update(eventoId, fd);
      message.success('Títulos guardados');
    } catch {
      message.error('Error al guardar los títulos');
    }
    setSavingTitulos(false);
  };

  const abrirNuevo = () => {
    setEditando(null);
    setLogoUrl(null);
    form.resetFields();
    form.setFieldsValue({ tipo_sponsor: 'sponsor', orden: 0, activo: true });
    setModalOpen(true);
  };

  const abrirEditar = (s) => {
    setEditando(s);
    setLogoUrl(s.url_imagen || null);
    form.setFieldsValue({
      nombre_sponsor: s.nombre_sponsor,
      tipo_sponsor: s.tipo_sponsor,
      url_link: s.url_link,
      orden: s.orden,
      activo: s.activo === 1,
    });
    setModalOpen(true);
  };

  const subirLogo = async (file) => {
    setUploadingLogo(true);
    try {
      const res = await sponsorService.uploadLogo(file);
      setLogoUrl(res.data.url_imagen);
      message.success('Logo subido');
    } catch {
      message.error('Error al subir el logo');
    }
    setUploadingLogo(false);
  };

  const handleGuardar = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return; // errores de validación ya se muestran
    }
    setSaving(true);
    try {
      const payload = {
        ...values,
        activo: values.activo ? 1 : 0,
        url_imagen: logoUrl,
        id_evento: eventoId,
      };
      if (editando) await sponsorService.update(editando.id_sponsor, payload);
      else await sponsorService.create(payload);
      message.success('Sponsor guardado');
      setModalOpen(false);
      cargar();
    } catch {
      message.error('Error al guardar el sponsor');
    }
    setSaving(false);
  };

  const handleEliminar = async (id) => {
    try {
      await sponsorService.delete(id);
      message.success('Sponsor eliminado');
      cargar();
    } catch {
      message.error('Error al eliminar el sponsor');
    }
  };

  const columnas = [
    {
      title: 'Logo', dataIndex: 'url_imagen', width: 90,
      render: u => u
        ? <img src={fixUrl(u)} alt="" style={{ maxHeight: 40, maxWidth: 80, objectFit: 'contain' }} />
        : '—',
    },
    { title: 'Nombre', dataIndex: 'nombre_sponsor' },
    {
      title: 'Nivel', dataIndex: 'tipo_sponsor', width: 150,
      render: t => NIVELES_SPONSOR.find(n => n.value === t)?.label || t,
    },
    { title: 'Orden', dataIndex: 'orden', width: 80, align: 'center' },
    {
      title: 'Activo', dataIndex: 'activo', width: 80, align: 'center',
      render: a => <Tag color={a ? 'green' : 'default'}>{a ? 'Sí' : 'No'}</Tag>,
    },
    {
      title: '', width: 110, align: 'right',
      render: (_, s) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => abrirEditar(s)} />
          <Popconfirm title="¿Eliminar sponsor?" okText="Sí" cancelText="No" onConfirm={() => handleEliminar(s.id_sponsor)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Card
      style={{ marginTop: 24 }}
      title={<Space><PictureOutlined /><span>Sponsors / Auspiciantes</span><Tag color="blue">{sponsors.length}</Tag></Space>}
      extra={<Button type="primary" icon={<PlusOutlined />} onClick={abrirNuevo}>Agregar sponsor</Button>}
    >
      {/* Títulos de cada nivel — cómo aparecen los encabezados en el micrositio */}
      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 4 }}>Títulos de las secciones en el micrositio</Text>
        <Text type="secondary" style={{ display: 'block', marginBottom: 12, fontSize: 13 }}>
          Personalizá el encabezado de cada nivel tal como se muestra en la web pública.
        </Text>
        <Row gutter={[12, 12]} align="bottom">
          <Col xs={24} sm={7}>
            <Text style={{ fontSize: 12, color: '#888' }}>Nivel Oro</Text>
            <Input value={titulos.gold} onChange={e => setTitulos(t => ({ ...t, gold: e.target.value }))} placeholder={SPONSOR_TITULOS_DEFAULT.gold} />
          </Col>
          <Col xs={24} sm={7}>
            <Text style={{ fontSize: 12, color: '#888' }}>Nivel Plata</Text>
            <Input value={titulos.silver} onChange={e => setTitulos(t => ({ ...t, silver: e.target.value }))} placeholder={SPONSOR_TITULOS_DEFAULT.silver} />
          </Col>
          <Col xs={24} sm={7}>
            <Text style={{ fontSize: 12, color: '#888' }}>Nivel Auspiciante</Text>
            <Input value={titulos.sponsor} onChange={e => setTitulos(t => ({ ...t, sponsor: e.target.value }))} placeholder={SPONSOR_TITULOS_DEFAULT.sponsor} />
          </Col>
          <Col xs={24} sm={3}>
            <Button type="primary" block loading={savingTitulos} onClick={handleGuardarTitulos}>Guardar</Button>
          </Col>
        </Row>
      </div>

      <Table
        scroll={{ x: 'max-content' }}
        rowKey="id_sponsor"
        dataSource={sponsors}
        columns={columnas}
        loading={loading}
        size="small"
        pagination={false}
        locale={{ emptyText: 'Sin sponsors cargados' }}
      />

      <Modal
        title={editando ? 'Editar sponsor' : 'Nuevo sponsor'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleGuardar}
        okText="Guardar"
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="nombre_sponsor" label="Nombre" rules={[{ required: true, message: 'El nombre es requerido' }]}>
            <Input placeholder="Ej: Banco Nación" />
          </Form.Item>

          <Form.Item label="Logo">
            <Upload
              showUploadList={false}
              accept="image/*"
              customRequest={({ file, onSuccess, onError }) =>
                subirLogo(file).then(() => onSuccess?.()).catch(onError)
              }
            >
              <Button icon={<UploadOutlined />} loading={uploadingLogo}>Subir logo</Button>
            </Upload>
            {logoUrl && (
              <div style={{ marginTop: 8 }}>
                <img src={fixUrl(logoUrl)} alt="logo" style={{ maxHeight: 50, maxWidth: 140, objectFit: 'contain', border: '1px solid #f0f0f0', borderRadius: 4, padding: 4 }} />
              </div>
            )}
          </Form.Item>

          <Form.Item name="tipo_sponsor" label="Nivel" rules={[{ required: true }]}>
            <Select
              options={NIVELES_SPONSOR.map(n => ({
                value: n.value,
                label: `${n.label}  ·  Tamaño img sugerido ${n.size}`,
              }))}
            />
          </Form.Item>

          <Form.Item name="url_link" label="Link (opcional)">
            <Input placeholder="https://..." />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="orden" label="Orden" tooltip="Menor = aparece primero">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="activo" label="Activo" valuePropName="checked">
                <Switch checkedChildren="Sí" unCheckedChildren="No" />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </Card>
  );
};


export default EventoForm;
